import React, { createContext, useContext, useEffect, useState } from "react";
import { deletePaymentMethod } from "@/api/user";
import { showNotification } from "@/utils/network";
import { useUserDataContext } from "@/store/UserProvider";
import { useTranslation } from "react-i18next";
import { getSavedPaymentMethods } from "./paymentOptionHelpers";

type ChoosePaymentOptionsStep = "" | "confirm" | "addNewCard";

interface ChoosePaymentOptionsContextValue {
  step: ChoosePaymentOptionsStep;
  newOption: string;
  isManageMode: boolean;
  deletingPaymentMethodId: string | null;
  pendingDeletePaymentMethodId: string | null;
  hasSavedCards: boolean;
  openConfirm: (newOption: string) => void;
  openAddNewCard: () => void;
  goBack: () => void;
  toggleManageMode: () => void;
  requestDeleteCard: (paymentMethodId: string) => void;
  cancelDeleteCard: () => void;
  confirmDeleteCard: () => Promise<void>;
}

const ChoosePaymentOptionsContext = createContext<
  ChoosePaymentOptionsContextValue | undefined
>(undefined);

export function ChoosePaymentOptionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { user, paymentOption, refreshUser } = useUserDataContext();

  const [step, setStep] = useState<ChoosePaymentOptionsStep>("");
  const [newOption, setNewOption] = useState("");
  const [isManageMode, setIsManageMode] = useState(false);
  const [deletingPaymentMethodId, setDeletingPaymentMethodId] = useState<
    string | null
  >(null);
  const [
    pendingDeletePaymentMethodId,
    setPendingDeletePaymentMethodId,
  ] = useState<string | null>(null);

  useEffect(() => {
    setStep("");
    setNewOption("");
    setIsManageMode(false);
    setDeletingPaymentMethodId(null);
    setPendingDeletePaymentMethodId(null);
  }, []);

  const hasSavedCards = getSavedPaymentMethods(user).length > 0;

  const confirmDeleteCard = async () => {
    const paymentMethodId = pendingDeletePaymentMethodId;
    if (!user) return;
    if (!paymentMethodId) return;

    const isLastCard = getSavedPaymentMethods(user).length === 1;

    setDeletingPaymentMethodId(paymentMethodId);

    try {
      await deletePaymentMethod({ payment_method_id: paymentMethodId });
      if (isLastCard) {
        setIsManageMode(false);
      }
      await refreshUser(true);
      setPendingDeletePaymentMethodId(null);
      showNotification({
        type: "success",
        text: t("paymentOptions.deletedPaymentMethod"),
      });
    } catch (e) {
      console.error(e);
      showNotification({
        type: "error",
        text: t("paymentOptions.unableToChangePaymentOption"),
      });
    } finally {
      setDeletingPaymentMethodId(null);
    }
  };

  return (
    <ChoosePaymentOptionsContext.Provider
      value={{
        step,
        newOption,
        isManageMode,
        deletingPaymentMethodId,
        pendingDeletePaymentMethodId,
        hasSavedCards,

        openConfirm: (nextOption) => {
          if (paymentOption === nextOption) return;

          setNewOption(nextOption);
          setStep("confirm");
        },
        openAddNewCard: () => setStep("addNewCard"),
        goBack: () => setStep(""),
        toggleManageMode: () => setIsManageMode((prev) => !prev),
        requestDeleteCard: (paymentMethodId) =>
          setPendingDeletePaymentMethodId(paymentMethodId),
        cancelDeleteCard: () => setPendingDeletePaymentMethodId(null),
        confirmDeleteCard,
      }}
    >
      {children}
    </ChoosePaymentOptionsContext.Provider>
  );
}

export function useChoosePaymentOptionsContext() {
  const ctx = useContext(ChoosePaymentOptionsContext);

  if (!ctx) {
    throw new Error(
      "useChoosePaymentOptionsContext must be used within ChoosePaymentOptionsProvider"
    );
  }

  return ctx;
}
